import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Boxes,
  Factory,
  Package,
  QrCode,
  ShoppingCart,
  BrainCircuit,
  Tags,
  Sparkles,
  Undo2,
  IndianRupee,
  BarChart3,
  Building2,
  UserSquare,
  ReceiptText,
  PackageOpen,
  ClipboardList,
  Layers,
  BookOpen,
  Warehouse,
  FlaskConical,
  ShieldQuestion,
  FileWarning,
  Mail,
} from 'lucide-react';

/**
 * Sidebar grouping. Two families of consoles in this app:
 *
 *  1. **Operational ERP** (groups: overview / stock / fulfilment / aftercare /
 *     insights) — the daily work surface. Visible to TENANT_ADMIN + future
 *     operator/manager/viewer roles, gated by per-tenant featureFlags.
 *
 *  2. **Workspace** — the customer's own admin console. TENANT_ADMIN manages
 *     their own organizations / members / warehouses / integrations / API keys
 *     / audit log. ZAFROI never sees this group as ZAFROI.
 *
 * The ZAFROI cross-tenant **Platform Admin** console used to be a third family
 * here; it moved to its own app (platform.zafroi.com) and PLATFORM_ADMINs are
 * bounced there on login (see homeForUser), so it no longer appears in the nav.
 */
export type NavGroup =
  | 'overview'
  | 'stock'
  | 'procurement'
  | 'fulfilment'
  | 'delivery'
  | 'aftercare'
  | 'insights'
  // Workspace sub-groups (Linear-style sectioning). All filter the
  // same way (TENANT_ADMIN, non-PLATFORM_ADMIN) — see filterNavForUser.
  | 'ws-core'
  | 'ws-developer'
  | 'ws-compliance';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  group: NavGroup;
  description?: string;
}

// ── Operational ERP — gated by per-tenant featureFlags ────────────────────
const operationalNav: NavItem[] = [
  { label: 'Dashboard',       to: '/dashboard',        icon: LayoutDashboard, group: 'overview',   description: 'Operational overview & KPIs' },
  { label: 'Guide',           to: '/guide',            icon: BookOpen,        group: 'overview',   description: 'Learn the platform end-to-end — start here' },
  { label: 'Inventory',       to: '/inventory',        icon: Boxes,           group: 'stock',      description: 'Raw materials, components, packaging' },
  { label: 'Categories',      to: '/inventory/categories', icon: Tags,        group: 'stock',      description: 'Organisational tags for items' },
  { label: 'Collections',     to: '/inventory/collections', icon: Sparkles,   group: 'stock',      description: 'Curated marketing groups — Gift, Diwali, seasonal edits' },
  { label: 'Packaging',       to: '/inventory/packaging', icon: PackageOpen,  group: 'stock',      description: 'Shipping boxes & fulfilment defaults' },
  { label: 'Finished Goods',  to: '/finished-goods',   icon: Package,         group: 'stock',      description: 'Ready-to-ship batches' },
  { label: 'Barcodes / QR',   to: '/barcodes',         icon: QrCode,          group: 'stock',      description: 'Generate, scan, print' },
  { label: 'Vendors',         to: '/vendors',          icon: Building2,       group: 'procurement', description: 'Supplier master · GSTIN, PAN, addresses' },
  { label: 'Raw Materials',   to: '/raw-materials',    icon: Layers,          group: 'procurement', description: 'Raw-material items — cotton, zip, button · stock on hand' },
  { label: 'Purchase Requests', to: '/purchase-requests', icon: ClipboardList, group: 'procurement', description: 'Raw-material requests · approval · vendor or open-market sourcing' },
  { label: 'Product Recipes', to: '/job-work/recipes', icon: Factory, group: 'procurement', description: 'How each finished good is made — multi-step route + per-step materials (job work)' },
  { label: 'Job Work Orders', to: '/job-work/orders', icon: Boxes, group: 'procurement', description: 'Run production steps — consume materials, produce output; in-house or outsourced' },
  { label: 'Purchase Orders', to: '/purchase-orders',  icon: PackageOpen,     group: 'procurement', description: 'Issue POs to vendors · receive against PO' },
  { label: 'Customers',       to: '/customers',        icon: UserSquare,      group: 'fulfilment', description: 'CRM records — name, GSTIN, contact' },
  { label: 'Sales Orders',    to: '/orders',           icon: ShoppingCart,    group: 'fulfilment', description: 'Customer orders pipeline' },
  { label: 'Invoices',        to: '/invoices',         icon: ReceiptText,     group: 'fulfilment', description: 'GST tax invoices · CGST/SGST/IGST split' },
  // Tracking folded into the Dispatcher → Dispatch tab (Moving / Late
  // filter + late-aware ETA). /tracking redirects there for old links.
  // ── Delivery — one Dispatcher control plane. Partners, Tiers & options,
  //    Routing rules, Quotes & audit, Dispatch, Labels & invoices and
  //    Analytics are all tabs inside it (no longer separate nav items).
  { label: 'Dispatcher',      to: '/dispatch/dispatcher', icon: BrainCircuit,  group: 'delivery',   description: 'Delivery control plane — partners, tiers & options, routing rules, quotes & audit, dispatch, labels & invoices, analytics' },
  { label: 'Cancel Requests', to: '/cancel-requests',  icon: FileWarning,     group: 'aftercare',  description: 'Customer-initiated cancels — review + auto-refund via portal' },
  { label: 'Return Requests', to: '/return-requests',  icon: Undo2,           group: 'aftercare',  description: 'Customer-initiated returns — review queue before physical pickup' },
  { label: 'Return Orders',   to: '/returns',          icon: Undo2,           group: 'aftercare',  description: 'Approved returns moving through scan-inward, QC, and disposition' },
  { label: 'Refunds',         to: '/refunds',          icon: IndianRupee,     group: 'aftercare',  description: 'Refund settlement' },
  { label: 'My Reviews',      to: '/my-reviews',       icon: ShieldQuestion,  group: 'aftercare',  description: 'Cancel + return requests assigned to you for decision' },
  { label: 'Reports',         to: '/reports',          icon: BarChart3,       group: 'insights',   description: 'Operational reports' },
];

// ── Workspace — TENANT_ADMIN view of their OWN tenant resources ──────────
// Same routes as platform-admin (backend scopes per tier) but tenant-friendly
// labels. Sub-grouped Linear-style: Workspace / Developer / Compliance.
// Reference leads the Developer block — it's the natural starting point for
// integration work.
const workspaceNav: NavItem[] = [
  // Workspace core. Account/identity (org, members, mobile-app) moved to the
  // platform console (ERP-DELOAD-003 / ADR-014); warehouses are operational.
  { label: 'Warehouses',       to: '/platform/warehouses',        icon: Warehouse,      group: 'ws-core',       description: 'Your fulfilment locations' },
  { label: 'Email senders',    to: '/settings/email',             icon: Mail,           group: 'ws-core',       description: 'Send customer email from your own domain or SMTP server — you own the sender' },

  // Developer/integration tools (api-clients/keys, connectors, webhooks, API
  // reference/logs, business errors, saved views, portal preview) moved to the
  // platform console (DEV-TOOLS-HOME / ERP-DELOAD-003 / ADR-014).

  // Compliance. Audit + support sessions moved to the platform console
  // (ERP-DELOAD-003 / ADR-014).
  { label: 'Test data',        to: '/settings/test-data',         icon: FlaskConical,   group: 'ws-compliance', description: 'Mark test records · run an auditable, restorable cleanup' },

  // Tax preferences live on each Organization profile under Tax & compliance
  // (rather than a separate Settings entry) — Zoho-style co-location.
];

// ── Platform Admin console — RETIRED ─────────────────────────────────────
//
// The cross-tenant control plane moved to its own app (platform.zafroi.com),
// the single source of truth. PLATFORM_ADMINs are bounced there on login (see
// homeForUser), so the ERP no longer carries a platform-admin nav. The
// underlying /platform/* routes stay mounted (router.tsx) for emergency
// direct-URL access and because several are shared with the Workspace
// (ws-compliance) nav — they're just off the daily nav now.

/** Combined for backward compatibility (e.g. Topbar breadcrumb lookup). */
export const navItems: NavItem[] = [
  ...operationalNav,
  ...workspaceNav,
];

export const navGroupLabels: Record<NavGroup, string> = {
  overview: 'Overview',
  stock: 'Stock',
  procurement: 'Procurement',
  fulfilment: 'Fulfilment',
  delivery: 'Delivery setup',
  aftercare: 'Post-sales',
  insights: 'Insights',
  // Workspace sub-sections.
  'ws-core':        'Workspace',
  'ws-developer':   'Developer',
  'ws-compliance':  'Compliance',
};

// ── Filtering ────────────────────────────────────────────────────────────

export interface NavUser {
  tier?: string;
  role?: string;
  featureFlags?: string[];
  /** Effective permission keys from /auth/me (e.g. 'orders.read'). When
   *  absent/empty we fail open — see filterNavForUser. */
  permissions?: string[];
}

/**
 * Nav module (top-level route segment) → the read permission that governs it,
 * mirroring the backend `@RbacResource` modules. Drives permission-aware nav:
 * a user with a role that lacks the read permission won't see the menu item.
 * Segments NOT listed here (job-work, my-reviews, guide, …) are not
 * RBAC-governed on the backend either, so they stay visible. `raw-materials`
 * and `return-requests` ride on their parent module's permission.
 */
const NAV_READ_PERMISSION: Record<string, string> = {
  inventory: 'inventory.read',
  'finished-goods': 'finished-goods.read',
  barcodes: 'barcodes.read',
  vendors: 'vendors.read',
  'raw-materials': 'inventory.read',
  'purchase-requests': 'purchase-requests.read',
  'purchase-orders': 'purchase-orders.read',
  customers: 'customers.read',
  orders: 'orders.read',
  invoices: 'invoices.read',
  dispatch: 'dispatch.read',
  'cancel-requests': 'cancel-requests.read',
  'return-requests': 'returns.read',
  returns: 'returns.read',
  refunds: 'refunds.read',
  reports: 'reports.read',
  'job-work': 'job-work.read',
};

/** PLATFORM_ADMIN → bounced out to the platform control-plane app (see
 *  ExitToPlatform). Everyone else → operational dashboard. */
export function homeForUser(user: NavUser | null): string {
  if (user?.tier === 'PLATFORM_ADMIN') return '/exit-to-platform';
  return '/dashboard';
}

/**
 * Returns the nav items the current user should see.
 *
 * - PLATFORM_ADMIN: no ERP nav — bounced to the platform app (see homeForUser).
 * - TENANT_ADMIN: operational ERP (filtered by featureFlags) + workspace.
 * - Other tenant roles (manager / operator / viewer): operational ERP only.
 * - Null user (dev API-key fallback): everything (permissive).
 */
export function filterNavForUser(items: NavItem[], user: NavUser | null): NavItem[] {
  if (!user) return items;

  const isPlatformAdmin = user.tier === 'PLATFORM_ADMIN';
  // UNIFIED-ACCESS S3 (ADR-028): tier is authoritative (role-driven per ADR-027);
  // dropped the redundant legacy-enum `|| user.role === 'admin'` fallback.
  const isTenantAdmin = user.tier === 'TENANT_ADMIN';
  const flags = new Set(user.featureFlags ?? []);
  // Per-user role permissions from /auth/me. When ABSENT/empty (older cached
  // session, dev API-key, or any resolution gap) we FAIL OPEN — the nav stays
  // feature-flag gated exactly as before and is never blanked. Tenant admins
  // hold every permission, so they bypass this gate.
  const perms = user.permissions;
  const permissionAware =
    !isTenantAdmin && Array.isArray(perms) && perms.length > 0;

  return items.filter((item) => {
    // PLATFORM_ADMINs are bounced to the platform app and carry no ERP nav.
    if (isPlatformAdmin) return false;
    // All ws-* sub-groups are TENANT_ADMIN only.
    if (item.group.startsWith('ws-')) return isTenantAdmin;
    // Operational groups
    if (item.group === 'overview') return true;
    // Match by full slug first; fall back to the top-level segment so a
    // sub-route (e.g. /inventory/categories) inherits its parent's flag
    // (/inventory). Adding new sub-views doesn't need a feature-flag entry.
    const slug = item.to.replace(/^\//, '');
    const top = slug.split('/')[0];

    // 1) Tenant subscription gate (per-tenant featureFlags) — unchanged.
    if (!flags.has(slug) && !flags.has(top)) return false;

    // 2) Per-user role gate — hide a module this user has no read permission
    //    for. Items with no governed permission stay visible (they mirror the
    //    backend, which doesn't RBAC-enforce them).
    if (permissionAware) {
      const need = NAV_READ_PERMISSION[top];
      if (need && !perms!.includes(need)) return false;
    }
    return true;
  });
}
